"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { HOME_ANIMATION } from "@/app/pages/homePage/utils/home.constants"
import useOrientation from "@/hooks/useOrientation"
import Topnav from "@/components/topnav"

import {
  getStoredNotifications,
  removeNotification,
  clearNotifications,
  subscribeToNotificationChanges,
  markNotificationAsRead,
  type MixtapeNotification,
} from "@/core/notifications/notificationEvents"

const formatTime = (value?: string) =>
  value
    ? new Date(value).toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "--:--"

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : ""

export default function NotificacionesPage() {
  const { isPortrait, isLandscape, isStandalone } = useOrientation()
  const [notifications, setNotifications] = useState<MixtapeNotification[]>([])
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    setNotifications(getStoredNotifications())

    return subscribeToNotificationChanges(() => {
      setNotifications(getStoredNotifications())
    })
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const handleDelete = (id: string) => {
    removeNotification(id)
  }

  const handleClear = () => {
    clearNotifications()
  }

  const handleOpen = (notification: MixtapeNotification) => {
    if (!notification.leida) markNotificationAsRead(notification.id)
  }

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden text-white">
      <Topnav />

      <div
        className={`
          inset-0
          flex
          w-full
          flex-col
          //overflow-hidden
          select-none
          ${
            isLandscape
              ? "h-[calc(100%-3.7rem)] rounded-xl border border-white/15 bg-[var(--movil-nav)] overscroll-y-auto"
              : ""
          }
          ${isPortrait ? "mb-0 h-[100dvh] pb-0 pt-12" : ""}
          ${isStandalone ? "mb-40 h-[100dvh]" : ""}
        `}
      >
        <div 
       
          className={`
            flex-1
            min-h-0
            h-full
            overflow-x-hidden
            overflow-y-scroll
            scrollbar-hide
           
          `}
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
            overscrollBehaviorX: "none",
          }}
        >
            

      <motion.div variants={HOME_ANIMATION}
                    initial="initial"
                    animate="animate"
                    exit="exit"
        className={`  motion-content
          flex h-full w-full flex-col overflow-hidden
          ${isLandscape ? "h-[calc(100%-3.7rem)] rounded-xl border border-white/10 bg-[var(--movil-nav)]" : ""}
          ${isPortrait ? "pb-16" : ""}
          ${isStandalone ? "pb-20 pt-18" : ""}
        `}
      >
        <header className="flex shrink-0 items-center justify-between px-5 pb-4">
          <div>
            <h1 className="text-xl font-bold tracking-tight">
              Notificaciones
            </h1>
            <p className="mt-1 text-xs text-white/40">
              Lo nuevo de las radios que sigues
            </p>
          </div>

          {notifications.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-semibold text-white/55 transition hover:bg-white/10 active:scale-95"
            >
              <i className="f7-icons text-xs">trash</i>
              Limpiar
            </button>
          )}
        </header>

        <div className="no-scrollbar flex h-full flex-col gap-3 overflow-y-auto px-4 pb-8"> {/* pb-28 */}
          {notifications.length === 0 ? (
            <div className="flex min-h-[22rem] flex-1 flex-col items-center justify-center rounded-[28px] border border-white/10 bg-white/[0.035] px-6 text-center">
              <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-white/5">
                <i className="f7-icons text-2xl text-white/45">bell</i>
              </div>

              <h2 className="text-base font-semibold">
                No tienes notificaciones
              </h2>

              <p className="mt-1 max-w-xs text-xs leading-relaxed text-white/35">
                Cuando sigas una radio, sus próximos programas aparecerán aquí.
              </p>
            </div>
          ) : (
            notifications.map((notification) => {
              const start = new Date(
                notification.inicio || notification.fecha
              ).getTime()

              const end = notification.fin
                ? new Date(notification.fin).getTime()
                : start

              const isLive = now >= start && now < end
              const remaining = Math.max(start - now, 0)
              const minutes = Math.floor(remaining / 60000)
              const image =
                notification.imagen?.trim() ||
                "/image/app/notificacion/mixtape.webp"

              return (
                <article
                  key={notification.id}
                  onClick={() => handleOpen(notification)}
                  className={`
                    relative isolate overflow-hidden rounded-[28px]
                    border shadow-2xl transition
                    ${notification.leida
                      ? "border-white/5"
                      : "border-white/20"
                    }
                  `}
                  style={{
                    background: notification.color
                      ? `linear-gradient(135deg, ${notification.color}cc, rgba(0,0,0,.92))`
                      : "rgba(255,255,255,.05)",
                  }}
                >
                  <img
                    src={image}
                    alt=""
                    className="absolute inset-0 -z-20 h-full w-full object-cover opacity-25 blur-xl"
                  />

                  <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/30 via-black/70 to-black/90" />

                  <div className="flex min-h-[170px] gap-3 p-3">
                    <img
                      src={image}
                      alt=""
                      className="h-[145px] w-28 shrink-0 rounded-2xl object-cover shadow-xl"
                    />

                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10">
                            <i className="f7-icons text-sm">
                              {isLive ? "waveform" : "bell_fill"}
                            </i>
                          </span>

                          <div className="min-w-0">
                            <p className="truncate text-[9px] font-bold uppercase tracking-[0.18em] text-white/45">
                              {notification.radioNombre || "Mixtape"}
                            </p>

                            <p className="mt-0.5 truncate text-[10px] font-medium text-white/70">
                              {isLive
                                ? "Ahora en directo"
                                : notification.titulo}
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleDelete(notification.id)
                          }}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-black/20 text-white/35 transition hover:text-white active:scale-90"
                          aria-label="Eliminar notificación"
                        >
                          <i className="f7-icons text-sm">xmark</i>
                        </button>
                      </div>

                      <div className="mt-4 flex-1">
                        <h2 className="line-clamp-2 text-base font-bold leading-tight">
                          {notification.descripcion}
                        </h2>

                        <p className="mt-1 text-xs leading-relaxed text-white/50">
                          {isLive
                            ? "Este programa está sonando ahora mismo."
                            : `Empieza a las ${formatTime(notification.inicio || notification.fecha)}`}
                        </p>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {isLive ? (
                          <a
                            href={notification.enlaceEvento || "/radio"}
                            onClick={(event) => event.stopPropagation()}
                            className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold text-black shadow-lg transition active:scale-95"
                          >
                            <i className="f7-icons text-sm">waveform</i>
                            Escuchar ahora
                          </a>
                        ) : (
                          <div className="flex items-center gap-2 rounded-full border border-white/20 bg-black/30 px-3 py-2">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                            <span className="text-xs font-bold tabular-nums">
                              {minutes > 0
                                ? `En ${minutes} min`
                                : "Comienza ahora"}
                            </span>
                          </div>
                        )}

                        <div className="ml-auto text-right text-[9px] text-white/35">
                          <span>
                            {formatDate(
                              notification.inicio || notification.fecha
                            )}
                          </span>
                          <span> · </span>
                          <span>{notification.radioNombre}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              )
            })
          )}
        </div>
      </motion.div>

        </div>

        </div>
    </div>
  )
}